import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAssistantRefresh } from '../hooks/useAssistantRefresh';
import { useSearchParams } from 'react-router-dom';
import { formatTaskKey,
  TASK_PRIORITY_LABEL,
  TASK_STATUS_LABEL,
  TaskStatus,
  type DepartmentSummary,
  type ProjectSummary,
  type TaskSummary,
  type UserSummary,
} from '@todo/shared';
import { cellStyle, downloadXlsx, HEADER_STYLE, XLSX_FILL, type StyleGrid } from '../lib/xlsx';
import { ApiError } from '../api/client';
import { taskApi } from '../api/task.api';
import { useTaskActions } from '../hooks/useTaskActions';
import { userApi } from '../api/user.api';
import { departmentApi } from '../api/department.api';
import { projectApi } from '../api/project.api';
import { MultiSelect } from '../components/MultiSelect';
import { SidePanel } from '../components/SidePanel';
import { TaskForm } from '../components/TaskForm';
import { TaskDetailPanel } from '../components/TaskDetailPanel';
import { TaskCard, TaskListError, TaskListEmpty } from '../components/TaskCard';
import { ALL_COLUMNS, TaskBoard } from '../components/TaskBoard';
import { BlockReasonModal } from '../components/BlockReasonModal';
import { useDragToStatus } from '../hooks/useDragToStatus';

type Scope = 'all' | 'assigned-to-me' | 'created-by-me' | 'unassigned';

const SCOPE_LABEL: Record<Scope, string> = {
  all: 'All',
  'assigned-to-me': 'Assigned to me',
  'created-by-me': 'Created by me',
  unassigned: 'Unassigned',
};

export default function TasksPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  /** Extra filters: a due-date window, plus multi-select assignees and
   *  departments - a task matches when it has ANY of the picked values. */
  const [dueOn, setDueOn] = useState({ from: '', to: '' });
  const [assignees, setAssignees] = useState<string[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [projects, setProjects] = useState<string[]>([]);
  const [people, setPeople] = useState<UserSummary[]>([]);
  const [departmentList, setDepartmentList] = useState<DepartmentSummary[]>([]);
  const [projectList, setProjectList] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  /** Multi-select statuses - a task matches when it is in ANY of them.
   *  Travels in the URL as ?status=DONE or ?status=PENDING,ONGOING so the
   *  old single-status deep links keep working unchanged. */
  const [statuses, setStatuses] = useState<TaskStatus[]>(() => {
    if (searchParams.get('open') === 'true' || searchParams.get('overdue') === 'true') return [];
    const s = searchParams.get('status');
    if (!s) return [];
    return s
      .split(',')
      .map((part) => part.trim())
      .filter((part): part is TaskStatus => part in TASK_STATUS_LABEL);
  });
  const [scope, setScope] = useState<Scope>(() => {
    const s = searchParams.get('scope');
    return s === 'assigned-to-me' || s === 'created-by-me' || s === 'unassigned' ? s : 'all';
  });
  /** Dashboard deep-links land here: "Open tasks" / "Overdue" both narrow by
   *  a STATUS SET rather than one exact status, so they get their own
   *  toggle instead of squeezing into the status dropdown. Mutually
   *  exclusive with it - picking either clears the other. */
  const [quick, setQuick] = useState<'' | 'open' | 'overdue'>(() => {
    if (searchParams.get('overdue') === 'true') return 'overdue';
    if (searchParams.get('open') === 'true') return 'open';
    return '';
  });
  const [panelOpen, setPanelOpen] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [viewing, setViewing] = useState<TaskSummary | null>(null);

  function replace(updated: TaskSummary) {
    setTasks((current) => current.map((t) => (t.id === updated.id ? updated : t)));
  }

  const actions = useTaskActions(replace, setFlash);
  const drag = useDragToStatus(
    (id) => tasks.find((t) => t.id === id),
    replace,
    setFlash,
  );

  /** The server-side filter set currently in force - built once, used by
   *  both the list load and the Excel export, so what exports is exactly
   *  what the filters describe. "Unassigned" stays a client-side view over
   *  the full list - the API has no such scope, and the visibility rule
   *  already limits it to your own tasks. */
  const buildQuery = useCallback(() => ({
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(statuses.length > 0 ? { statuses: statuses.join(',') } : {}),
    // "Open" is PENDING/ONGOING/BLOCKED together - both quick filters
    // narrow to that set; Overdue further pins the due window to
    // "before right now", the same rule the dashboard's own count uses.
    ...(quick === 'open' || quick === 'overdue' ? { open: true } : {}),
    ...(assignees.length > 0 ? { assigneeIds: assignees.join(',') } : {}),
    ...(departments.length > 0 ? { departmentIds: departments.join(',') } : {}),
    ...(projects.length > 0 ? { projectIds: projects.join(',') } : {}),
    // Dates are whole days: from-midnight, and to-EXCLUSIVE next midnight.
    ...(quick !== 'overdue' && dueOn.from ? { dueFrom: new Date(`${dueOn.from}T00:00:00`).toISOString() } : {}),
    ...(quick === 'overdue'
      ? { dueTo: new Date().toISOString() }
      : dueOn.to
        ? { dueTo: new Date(new Date(`${dueOn.to}T00:00:00`).getTime() + 86_400_000).toISOString() }
        : {}),
    scope: (scope === 'unassigned' ? 'all' : scope) as 'all' | 'assigned-to-me' | 'created-by-me',
    /*
     * PARENTS ONLY on this board.
     *
     * Sub-tasks used to come back mixed into the same flat list, which
     * went wrong in two ways. The count said "83 tasks" when it meant 83
     * ROWS - a parent with ten children counted eleven. And TaskBoard's
     * nestSubtasks() only nests a child when its parent is in the SAME
     * loaded page, so a parent on page 2 with children on page 1 left
     * those children stranded as their own cards. Which cards stranded
     * depended on where the page boundary happened to fall.
     *
     * The children are fetched per parent below, so the cards still show
     * their checklist - they are just no longer competing for a slot in
     * the column.
     */
    topLevel: true,
  }), [search, statuses, quick, scope, assignees, departments, projects, dueOn]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await taskApi.list({
        ...buildQuery(),
        page,
        pageSize: PAGE_SIZE,
      });

      /*
       * Pull in each parent's sub-tasks so the card can draw its
       * checklist. Same approach My Day already uses: the board's own
       * query excludes them, so they have to arrive some other way.
       *
       * Only for parents that HAVE children, so a page of childless
       * tasks costs no extra calls at all.
       */
      const parentIds = res.data
        .filter((t) => t.subtaskCount > 0)
        .map((t) => t.id);

      let withChildren = res.data;
      if (parentIds.length > 0) {
        const pages = await Promise.all(
          parentIds.map((parentId) => taskApi.list({ parentId, pageSize: 100 })),
        );
        withChildren = [...res.data, ...pages.flatMap((pg) => pg.data)];
      }

      setTasks(withChildren);
      /* The total is the SERVER's count of top-level rows, never
       * withChildren.length - that would add the children back into the
       * number the filter just took them out of. */
      setTotal(res.meta?.total ?? res.data.length);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load tasks');
    } finally {
      setLoading(false);
    }
  }, [buildQuery, page]);

  // Any filter change starts back at page 1 - page 3 of a different query
  // is meaningless.
  useEffect(() => { setPage(1); }, [search, statuses, quick, scope, assignees, departments, projects, dueOn]);

  // People and departments for their filter dropdowns, once.
  useEffect(() => {
    void userApi.list({ pageSize: 100 }).then((res) => setPeople(res.data)).catch(() => undefined);
    void departmentApi.list().then(setDepartmentList).catch(() => undefined);
    void projectApi.list().then(setProjectList).catch(() => undefined);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => void load(), 250);
    return () => clearTimeout(timer);
  }, [load]);

  // The AI chat can change data server-side; refetch when it says so.
  useAssistantRefresh(load);

  /** Keep the URL honest so Dashboard deep links and refreshes both work. */
  function applyScope(next: Scope) {
    setScope(next);
    setSearchParams((p) => {
      if (next === 'all') p.delete('scope'); else p.set('scope', next);
      return p;
    }, { replace: true });
  }

  function applyStatuses(next: TaskStatus[]) {
    setStatuses(next);
    setQuick('');
    setSearchParams((p) => {
      p.delete('open');
      p.delete('overdue');
      if (next.length === 0) p.delete('status'); else p.set('status', next.join(','));
      return p;
    }, { replace: true });
  }

  /** Dashboard deep-link target for the Open/Overdue quick filters. */
  function applyQuick(next: '' | 'open' | 'overdue') {
    setQuick(next);
    setStatuses([]);
    if (next === 'overdue') setDueOn({ from: '', to: '' });
    setSearchParams((p) => {
      p.delete('status');
      p.delete('open');
      p.delete('overdue');
      if (next) p.set(next, 'true');
      return p;
    }, { replace: true });
  }

  const [exporting, setExporting] = useState(false);

  /**
   * Everything the current filters match - not just the visible page - as
   * one .xlsx. Pages are fetched server-side with the same query the list
   * uses, so the export can never disagree with what is on screen.
   */
  async function exportToExcel() {
    if (exporting) return;
    setExporting(true);
    try {
      const pageSize = 100;
      let all: TaskSummary[] = [];
      for (let p = 1; p <= 50; p++) {
        /*
         * topLevel: false - the export DOES include sub-tasks, unlike the
         * board above it.
         *
         * The board hides them because a column is a place to work and a
         * child belongs inside its parent's card. A spreadsheet is a
         * record: a row silently missing from it is worse than a row the
         * reader did not expect, and nothing on the sheet would reveal
         * the omission. Each sub-task carries its own assignee, due date
         * and status, so it is a real line of work to report on.
         *
         * The parent is identified per row, so the hierarchy is not lost.
         */
        const res = await taskApi.list({
          ...buildQuery(), topLevel: false, page: p, pageSize,
        });
        all = all.concat(res.data);
        const totalRows = res.meta?.total ?? res.data.length;
        if (all.length >= totalRows || res.data.length < pageSize) break;
      }
      if (scope === 'unassigned') {
        all = all.filter((t) => t.assignees.length === 0);
      }

      // Dates only - the time of day is noise in a report.
      const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '');
      // No explicit lead on a single-assignee task means that person IS the lead.
      const leadOf = (t: TaskSummary) =>
        t.assignees.find((a) => a.isLead)?.displayName ??
        (t.assignees.length === 1 ? t.assignees[0].displayName : '');
      const grid = [
        ['Task Key', 'Title', 'Status', 'Priority', 'Department', 'Project', 'Created by',
         'Assignees', 'Lead', 'Due', 'Created', 'Last activity', 'Sub-tasks done', 'Details'],
        ...all.map((t) => [
          formatTaskKey(t.taskKey),
          t.title,
          TASK_STATUS_LABEL[t.status],
          TASK_PRIORITY_LABEL[t.priority],
          t.departmentName ?? '',
          t.projectName ?? '',
          t.createdByName,
          t.assignees.map((a) => a.displayName).join(', '),
          leadOf(t),
          when(t.dueAt),
          when(t.createdAt),
          when(t.lastActivityAt),
          t.subtaskCount > 0 ? `${t.subtaskDoneCount}/${t.subtaskCount}` : '',
          t.details ?? '',
        ]),
      ];
      // Whole row tinted by status; Title (col 1) bold; bold header row.
      const STATUS_FILL: Record<TaskStatus, number> = {
        DRAFT: XLSX_FILL.GRAY,
        PENDING: XLSX_FILL.SLATE,
        ONGOING: XLSX_FILL.AMBER,
        BLOCKED: XLSX_FILL.RED,
        DONE: XLSX_FILL.GREEN,
        CANCELLED: XLSX_FILL.GRAY,
      };
      const header = grid[0] ?? [];
      const styles: StyleGrid = [
        header.map(() => HEADER_STYLE),
        ...all.map((t) =>
          header.map((_, col) =>
            // Title: bold on the plain background; every other cell carries
            // the row's status tint.
            col === 1 ? cellStyle(undefined, true) : cellStyle(STATUS_FILL[t.status]),
          ),
        ),
      ];
      // Auto-fit-ish column widths from the longest value, within bounds.
      const colWidths = header.map((_, col) => {
        const longest = grid.reduce((max, row) => Math.max(max, (row[col] ?? '').length), 0);
        return Math.min(60, Math.max(10, Math.round(longest * 1.1) + 2));
      });
      downloadXlsx(`Daily Tasks-${new Date().toISOString().slice(0, 10)}`, 'Tasks', grid, styles, {
        colWidths,
        freezeHeader: true,
        autoFilter: true,
      });
      setFlash(`Exported ${all.length} ${all.length === 1 ? 'task' : 'tasks'} to Excel.`);
    } catch {
      setFlash('Could not export the tasks - try again.');
    } finally {
      setExporting(false);
    }
  }

  const visible = useMemo(
    () => (scope === 'unassigned' ? tasks.filter((t) => t.assignees.length === 0) : tasks),
    [scope, tasks],
  );

  const filtered = search.trim() !== '' || statuses.length > 0 || quick !== '' || scope !== 'all' || assignees.length > 0 || departments.length > 0 || projects.length > 0 || dueOn.from !== '' || dueOn.to !== '';

  return (
    <div className="mx-auto px-8 py-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Tasks</h1>
          <p className="mt-1 text-sm text-slate-500">
            {loading ? 'Loading...' : `${total} ${total === 1 ? 'task' : 'tasks'}`}
            {filtered && !loading && (
              <button
                type="button"
                onClick={() => { setSearch(''); applyStatuses([]); applyQuick(''); applyScope('all'); setAssignees([]); setDepartments([]); setProjects([]); setDueOn({ from: '', to: '' }); }}
                className="ml-2 font-medium text-brand-600 hover:text-brand-700"
              >
                Clear filters
              </button>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPanelOpen(true)}
          className="rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-brand-700"
        >
          Create Task
        </button>
      </header>

      {flash && (
        <div role="status" className="mt-5 flex items-start justify-between gap-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800 ring-1 ring-emerald-200">
          <span>{flash}</span>
          <button type="button" onClick={() => setFlash(null)} aria-label="Dismiss"
                  className="text-emerald-600 hover:text-emerald-800">&times;</button>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title or task key"
          className="w-56 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
        />

        {/* Segmented control: one background, the active tab floats on top.
            "Unassigned" has no chip of its own any more (the Export button
            took its slot) but still works as a Dashboard deep-link:
            /tasks?scope=unassigned filters exactly as before. */}
        <div className="flex items-center gap-0.5 rounded-lg bg-slate-100 p-1">
          {(['all', 'assigned-to-me', 'created-by-me'] as Scope[]).map((value) => (
            <button key={value} type="button" onClick={() => applyScope(value)}
              className={[
                'rounded-md px-3 py-1.5 text-xs font-semibold transition',
                scope === value
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700',
              ].join(' ')}>
              {SCOPE_LABEL[value]}
            </button>
          ))}
        </div>

        {/* The whole filtered set, downloaded as a real Excel workbook. */}
        <button
          type="button"
          onClick={() => void exportToExcel()}
          disabled={exporting || loading}
          title="Download every task matching the current filters as an Excel file"
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
        >
          {exporting ? 'Exporting…' : '↓ Export Excel'}
        </button>

        {/* Dashboard deep-links land here - "Open tasks" / "Overdue" are a
            status SET, not one exact status, so they get their own toggle
            instead of the dropdown below. */}
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={() => applyQuick(quick === 'open' ? '' : 'open')}
            className={[
              'rounded-full px-3 py-1.5 text-xs font-semibold ring-1 transition',
              quick === 'open'
                ? 'bg-slate-900 text-white ring-slate-900'
                : 'bg-white text-slate-600 ring-slate-300 hover:bg-slate-50',
            ].join(' ')}>
            Open only
          </button>
          <button type="button" onClick={() => applyQuick(quick === 'overdue' ? '' : 'overdue')}
            className={[
              'rounded-full px-3 py-1.5 text-xs font-semibold ring-1 transition',
              quick === 'overdue'
                ? 'bg-red-600 text-white ring-red-600'
                : 'bg-white text-red-600 ring-red-200 hover:bg-red-50',
            ].join(' ')}>
            Overdue
          </button>
        </div>

        {/* Multi-select status: a task matches when it is in ANY of the
            picked statuses, same manners as the assignee / department /
            project filters beside it. */}
        <MultiSelect
          placeholder="Any status"
          options={Object.values(TaskStatus)
            .filter((value) => value !== 'DRAFT')
            .map((value) => ({ value, label: TASK_STATUS_LABEL[value] }))}
          selected={statuses}
          onChange={(next) => applyStatuses(next as TaskStatus[])}
        />

        {/* Multi-select: a task matches when it has ANY picked assignee /
            sits in ANY picked department. */}
        <MultiSelect
          placeholder="Any assignee"
          options={people.map((p) => ({ value: p.id, label: p.displayName }))}
          selected={assignees}
          onChange={setAssignees}
        />

        <MultiSelect
          placeholder="Any department"
          options={departmentList.map((d) => ({ value: d.id, label: d.name }))}
          selected={departments}
          onChange={setDepartments}
        />

        <MultiSelect
          placeholder="Any project"
          options={projectList.map((p) => ({ value: p.id, label: p.name }))}
          selected={projects}
          onChange={setProjects}
        />

        <label className="flex items-center gap-1.5 text-xs text-slate-500">
          Due
          <input type="date" value={dueOn.from} disabled={quick === 'overdue'}
            onChange={(e) => setDueOn((d) => ({ ...d, from: e.target.value }))}
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-700 outline-none transition focus:border-brand-500 disabled:bg-slate-50 disabled:text-slate-400" />
          –
          <input type="date" value={dueOn.to} disabled={quick === 'overdue'}
            onChange={(e) => setDueOn((d) => ({ ...d, to: e.target.value }))}
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-700 outline-none transition focus:border-brand-500 disabled:bg-slate-50 disabled:text-slate-400" />
        </label>
      </div>

      <div className="mt-6 space-y-4">
        {error ? (
          <TaskListError message={error} onRetry={() => void load()} />
        ) : !loading && visible.length === 0 ? (
          <TaskListEmpty
            title="No tasks here"
            hint={filtered ? 'Try clearing the filters.' : 'Create the first one and assign it to someone.'}
          />
        ) : (
          <>
            {/* All 5 statuses, not just My Day's 4 working columns - the
                status filter above can ask for Cancelled directly, and a
                matching task needs somewhere on the board to land. */}
            <TaskBoard
              tasks={visible}
              loading={loading}
              columns={ALL_COLUMNS}
              doneCap={10}
              onDropTask={drag.onDropTask}
              renderCard={(task, subtasks) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  subtasks={subtasks}
                  busy={actions.busyId === task.id || drag.busyId === task.id}
                  onOpen={setViewing}
                  onToggleDone={(t) => void actions.toggleDone(t)}
                  onToggleImportant={(t) => void actions.toggleImportant(t)}
                  draggable
                />
              )}
            />

            {/* Pagination - server-side, so 500 tasks stay 10 quick pages. */}
            {total > PAGE_SIZE && (
              <div className="flex items-center justify-center gap-3 pt-1">
                <button type="button" disabled={page === 1 || loading}
                  onClick={() => setPage((n) => n - 1)}
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50 disabled:opacity-40">
                  &larr; Previous
                </button>
                <span className="text-xs tabular-nums text-slate-500">
                  Page {page} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
                </span>
                <button type="button" disabled={page >= Math.ceil(total / PAGE_SIZE) || loading}
                  onClick={() => setPage((n) => n + 1)}
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50 disabled:opacity-40">
                  Next &rarr;
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {drag.blocking && (
        <BlockReasonModal
          task={drag.blocking}
          reason={drag.blockReason}
          busy={drag.busyId === drag.blocking.id}
          onReasonChange={drag.setBlockReason}
          onConfirm={drag.confirmBlock}
          onCancel={drag.cancelBlock}
        />
      )}
      <SidePanel
        open={panelOpen}
        title="Create Task"
        description="Describe the work and choose who does it."
        closeOnBackdropClick={false}
        onClose={() => setPanelOpen(false)}
      >
        <TaskForm
          onCancel={() => setPanelOpen(false)}
          onCreated={(task) => {
            setPanelOpen(false);
            setFlash(
              task.assignees.length > 0
                ? `${formatTaskKey(task.taskKey)} created and sent to ${task.assignees.length} ${task.assignees.length === 1 ? 'person' : 'people'}.`
                : `${formatTaskKey(task.taskKey)} created.`,
            );
            void load();
          }}
        />
      </SidePanel>

      {viewing && (
        <TaskDetailPanel
          task={viewing}
          onClose={() => setViewing(null)}
          onChanged={(t) => { replace(t); setViewing(t); }}
        />
      )}
    </div>
  );
}
